/* eslint-disable react-refresh/only-export-components -- контекст и useMax в одном модуле */
import React, {
  useEffect,
  createContext,
  useContext,
  useMemo,
  useCallback,
  useState,
  useRef,
} from "react";
import { showAppAlert, showAppConfirm } from "@/shared/utils/appDialog";

declare global {
  interface Window {
    WebApp?: MaxWebApp;
  }
}

interface MaxUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  photo_url?: string;
}

interface MaxWebApp {
  ready: () => void;
  close: () => void;
  expand?: () => void;
  minimize?: () => void;

  initData: string;
  initDataUnsafe?: {
    user?: MaxUser;
    chat?: { id: number; type: string };
    start_param?: string;
    query_id?: string;
    auth_date?: number;
    hash?: string;
  };
  platform: "ios" | "android" | "desktop" | "web";
  version: string;

  colorScheme: "light" | "dark";

  MainButton: {
    text: string;
    color: string;
    textColor: string;
    isVisible: boolean;
    isActive: boolean;
    show: () => void;
    hide: () => void;
    setText: (text: string) => void;
    setParams: (params: {
      color?: string;
      textColor?: string;
      isActive?: boolean;
    }) => void;
    onClick: (callback: () => void) => void;
    offClick: (callback: () => void) => void;
    enable: () => void;
    disable: () => void;
  };

  BackButton: {
    isVisible: boolean;
    show: () => void;
    hide: () => void;
    onClick: (callback: () => void) => void;
    offClick: (callback: () => void) => void;
  };

  HapticFeedback?: {
    impactOccurred: (
      style: "light" | "medium" | "heavy" | "rigid" | "soft",
      disableVibrationFallback?: boolean
    ) => void;
    notificationOccurred: (
      type: "error" | "success" | "warning",
      disableVibrationFallback?: boolean
    ) => void;
    selectionChanged: (disableVibrationFallback?: boolean) => void;
  };

  showPopup: (
    params: {
      title?: string;
      message: string;
      buttons?: Array<{
        id?: string;
        type?: "default" | "ok" | "close" | "cancel" | "destructive";
        text?: string;
      }>;
    },
    callback?: (buttonId: string) => void
  ) => void;

  showAlert: (message: string, callback?: () => void) => void;
  showConfirm: (
    message: string,
    callback?: (confirmed: boolean) => void
  ) => void;

  sendData: (data: string) => void;

  onEvent: (
    eventType: string,
    callback: (...args: unknown[]) => void
  ) => void;
  offEvent: (
    eventType: string,
    callback: (...args: unknown[]) => void
  ) => void;

  requestContact: () => Promise<string>;

  enableClosingConfirmation: () => void;
  disableClosingConfirmation: () => void;

  openLink: (url: string) => void;
  openMaxLink: (url: string) => void;
}

interface MaxContextType {
  webApp: MaxWebApp | null;
  user: MaxUser | null;
  chatId?: number;
  chatType?: string;
  platform: string;
  version: string;
  isDark: boolean;
  ready: boolean;
  initData: string;
  haptics?: {
    impactOccurred: (
      style: "light" | "medium" | "heavy" | "rigid" | "soft"
    ) => void;
    notificationOccurred: (type: "error" | "success" | "warning") => void;
    selectionChanged: () => void;
  };
  mainButton: {
    show: (text?: string) => void;
    hide: () => void;
    enable: () => void;
    disable: () => void;
    setText: (text: string) => void;
    setColor: (color: string) => void;
    setTextColor: (color: string) => void;
    onClick: (callback: () => void) => () => void;
  };
  backButton: {
    show: () => void;
    hide: () => void;
    onClick: (callback: () => void) => () => void;
  };
  notifications: {
    showAlert: (message: string) => Promise<void>;
    showConfirm: (message: string) => Promise<boolean>;
    showPopup: (params: {
      title?: string;
      message: string;
      buttons?: Array<{
        id?: string;
        type?: "default" | "ok" | "close" | "cancel" | "destructive";
        text?: string;
      }>;
    }) => Promise<string>;
  };
  sendData: (data: object) => void;
  close: () => void;
  requestContact: () => Promise<string>;
  enableClosingConfirmation: () => void;
  disableClosingConfirmation: () => void;
  openLink: (url: string) => void;
  openMaxLink: (url: string) => void;
}

const MaxContext = createContext<MaxContextType | null>(null);

export const useMax = () => {
  return useContext(MaxContext);
};

export const MaxProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [ready, setReady] = useState(false);
  const [, setInitDataPass] = useState(0);
  const initPassTimerRef = useRef<number | undefined>(undefined);

  const webApp =
    typeof window !== "undefined" && window.WebApp ? window.WebApp : null;
  const user = webApp?.initDataUnsafe?.user || null;
  const chat = webApp?.initDataUnsafe?.chat;
  const chatId = chat?.id;
  const chatType = chat?.type;

  useEffect(() => {
    if (!webApp) {
      console.warn("Max WebApp is not available");
      return;
    }

    try {
      webApp.ready();
      queueMicrotask(() => {
        setReady(true);
        initPassTimerRef.current = window.setTimeout(
          () => setInitDataPass((n) => n + 1),
          0
        );
      });
    } catch (error) {
      console.error("Error initializing Max WebApp:", error);
    }
    return () => {
      if (initPassTimerRef.current !== undefined) {
        clearTimeout(initPassTimerRef.current);
        initPassTimerRef.current = undefined;
      }
    };
  }, [webApp]);

  const haptics = useMemo(() => {
    if (!webApp?.HapticFeedback) return undefined;

    return {
      impactOccurred: (
        style: "light" | "medium" | "heavy" | "rigid" | "soft"
      ) => {
        if (webApp?.HapticFeedback) {
          webApp.HapticFeedback.impactOccurred(style);
        }
      },
      notificationOccurred: (type: "error" | "success" | "warning") => {
        if (webApp?.HapticFeedback) {
          webApp.HapticFeedback.notificationOccurred(type);
        }
      },
      selectionChanged: () => {
        if (webApp?.HapticFeedback) {
          webApp.HapticFeedback.selectionChanged();
        }
      },
    };
  }, [webApp]);

  const mainButton = useMemo(
    () => ({
      show: (text?: string) => {
        if (webApp?.MainButton) {
          if (text) webApp.MainButton.setText(text);
          webApp.MainButton.show();
        }
      },
      hide: () => {
        if (webApp?.MainButton) webApp.MainButton.hide();
      },
      enable: () => {
        if (webApp?.MainButton) webApp.MainButton.enable();
      },
      disable: () => {
        if (webApp?.MainButton) webApp.MainButton.disable();
      },
      setText: (text: string) => {
        if (webApp?.MainButton) webApp.MainButton.setText(text);
      },
      setColor: (color: string) => {
        if (webApp?.MainButton) {
          webApp.MainButton.setParams({ color });
        }
      },
      setTextColor: (color: string) => {
        if (webApp?.MainButton) {
          webApp.MainButton.setParams({ textColor: color });
        }
      },
      onClick: (callback: () => void) => {
        if (webApp?.MainButton) {
          webApp.MainButton.onClick(callback);
          return () => webApp.MainButton?.offClick(callback);
        }
        return () => {};
      },
    }),
    [webApp]
  );

  const backButton = useMemo(
    () => ({
      show: () => {
        if (webApp?.BackButton) webApp.BackButton.show();
      },
      hide: () => {
        if (webApp?.BackButton) webApp.BackButton.hide();
      },
      onClick: (callback: () => void) => {
        if (webApp?.BackButton) {
          webApp.BackButton.onClick(callback);
          return () => webApp.BackButton?.offClick(callback);
        }
        return () => {};
      },
    }),
    [webApp]
  );

  const notifications = useMemo(
    () => ({
      // В Max нет нативных alert/confirm — всегда кастомная модалка приложения.
      showAlert: (message: string): Promise<void> => showAppAlert(message),
      showConfirm: (message: string): Promise<boolean> =>
        showAppConfirm(message),
      showPopup: async (params: {
        title?: string;
        message: string;
        buttons?: Array<{
          id?: string;
          type?: "default" | "ok" | "close" | "cancel" | "destructive";
          text?: string;
        }>;
      }): Promise<string> => {
        const buttons = params.buttons ?? [];
        const hasCancel = buttons.some(
          (b) => b.type === "cancel" || b.type === "close"
        );
        const destructive = buttons.find((b) => b.type === "destructive");
        const primary =
          destructive ??
          buttons.find((b) => b.type === "default" || b.type === "ok") ??
          buttons[0];

        if (!hasCancel && buttons.length <= 1) {
          await showAppAlert(params.message, {
            title: params.title,
            okLabel: primary?.text,
          });
          return primary?.id ?? "ok";
        }

        const ok = await showAppConfirm(params.message, {
          title: params.title,
          confirmLabel: primary?.text,
          cancelLabel: buttons.find(
            (b) => b.type === "cancel" || b.type === "close"
          )?.text,
          destructive: Boolean(destructive),
        });
        if (!ok) {
          return (
            buttons.find((b) => b.type === "cancel" || b.type === "close")
              ?.id ?? "cancel"
          );
        }
        return primary?.id ?? "ok";
      },
    }),
    []
  );

  const sendData = useCallback(
    (data: object) => {
      if (webApp) {
        webApp.sendData(JSON.stringify(data));
      } else {
        console.log("Max sendData (mock):", data);
      }
    },
    [webApp]
  );

  const requestContact = useCallback((): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!webApp) {
        reject(new Error("WebApp not available"));
        return;
      }

      const timeoutId = setTimeout(() => {
        reject(new Error("Phone request timeout"));
      }, 30000);

      if (typeof webApp.requestContact !== "function") {
        clearTimeout(timeoutId);
        reject(new Error("requestContact method not available"));
        return;
      }

      webApp
        .requestContact()
        .then((result: unknown) => {
          clearTimeout(timeoutId);

          let phone: string = "";

          if (typeof result === "string") {
            phone = result;
          } else if (result && typeof result === "object") {
            const r = result as Record<string, unknown>;
            const contact = r.contact as Record<string, unknown> | undefined;
            const raw =
              r.phone ??
              r.phone_number ??
              r.phoneNumber ??
              contact?.phone_number ??
              contact?.phone ??
              "";
            phone = typeof raw === "string" ? raw : String(raw ?? "");
          }

          phone = phone.replace(/[^\d+]/g, "");

          if (phone) {
            resolve(phone);
          } else {
            reject(new Error("No phone number received in response"));
          }
        })
        .catch((error: unknown) => {
          clearTimeout(timeoutId);
          console.error("RequestContact error:", error);
          reject(
            error instanceof Error
              ? error
              : new Error("Failed to request phone number")
          );
        });
    });
  }, [webApp]);

  const value: MaxContextType = {
    webApp,
    user,
    chatId,
    chatType,
    platform: webApp?.platform || "web",
    version: webApp?.version || "0.0",
    isDark: webApp?.colorScheme === "dark",
    ready,
    initData: webApp?.initData || "",
    haptics,
    mainButton,
    backButton,
    notifications,
    sendData,
    close: () => webApp?.close(),
    requestContact,
    enableClosingConfirmation: () => webApp?.enableClosingConfirmation(),
    disableClosingConfirmation: () => webApp?.disableClosingConfirmation(),
    openLink: (url: string) => webApp?.openLink(url),
    openMaxLink: (url: string) => webApp?.openMaxLink(url),
  };

  return <MaxContext.Provider value={value}>{children}</MaxContext.Provider>;
};
